<template>
  <section :wxid="$options.name" class="giveaway-graph-container">
    <figure>
      <wx-chart-line-graph
        v-if="giveawayGraph && giveawayGraph.products.length > 0"
        css-classes="canvas-container"
        :chart-data="giveawayChartData"
        :chart-options="giveawayChartOptions"
      />
    </figure>
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import i18n from "@/i18n";
import PackageFeatures from "@/components/PackageFeatures";
import StyleValues from "@/styles/_values.scss";
import WxChartLineGraph from "@/components/ui/WxChartLineGraph.vue";
import { convertKilogramTo, convertLiterTo, convertMeterTo, getUnitLabel } from "@/UnitUtils";

export default {
  name: "SpcGiveawayGraph",
  components: {
    WxChartLineGraph,
  },
  props: {
    productIndex: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      currentMillisUTC: 0,
    };
  },
  computed: {
    ...mapGetters("user", ["theme"]),
    ...mapGetters("packages", ["activePuHasRequiredFeature"]),
    ...mapGetters("dashboard", ["giveawayGraph", "activeProductionUnit", "averageProductGiveaway"]),
    isGiveawaySamplingModeActive() {
      if (!this.activeProductionUnit) return false;
      return (
        this.activePuHasRequiredFeature(PackageFeatures.giveaway) &&
        this.activeProductionUnit.giveaway_sampling_configuration !== null
      );
    },
    convertedDataPoints() {
      return this.product.data_points.map((dp) => {
        if (!this.averageProductGiveaway?.unit || this.averageProductGiveaway.unit === "count") return dp;
        const point = { ...dp };
        switch (point.unit) {
          case "KILOGRAM":
            point.value = convertKilogramTo(dp.value, this.averageProductGiveaway.unit);
            break;
          case "LITRE":
            point.value = convertLiterTo(dp.value, this.averageProductGiveaway.unit);
            break;
          case "METER":
            point.value = convertMeterTo(dp.value, this.averageProductGiveaway.unit);
            break;
        }
        return point;
      });
    },
    convertedTarget() {
      if (!this.product.target_value) return null;
      if (!this.averageProductGiveaway?.unit || this.averageProductGiveaway.unit === "count")
        return this.product.target_value;
      switch (this.product.target_unit) {
        case "KILOGRAM":
          return convertKilogramTo(this.product.target_value, this.averageProductGiveaway.unit);
        case "LITRE":
          return convertLiterTo(this.product.target_value, this.averageProductGiveaway.unit);
        case "METER":
          return convertMeterTo(this.product.target_value, this.averageProductGiveaway.unit);
        default:
          return this.product.target_value;
      }
    },
    convertedUpperTolerance() {
      if (!this.product.upper_tolerance) return null;
      if (!this.averageProductGiveaway?.unit || this.averageProductGiveaway.unit === "count")
        return this.product.upper_tolerance;
      switch (this.product.target_unit) {
        case "KILOGRAM":
          return convertKilogramTo(this.product.upper_tolerance, this.averageProductGiveaway.unit);
        case "LITRE":
          return convertLiterTo(this.product.upper_tolerance, this.averageProductGiveaway.unit);
        case "METER":
          return convertMeterTo(this.product.upper_tolerance, this.averageProductGiveaway.unit);
        default:
          return this.product.upper_tolerance;
      }
    },
    convertedLowerTolerance() {
      if (!this.product.lower_tolerance) return null;
      if (!this.averageProductGiveaway?.unit || this.averageProductGiveaway.unit === "count")
        return this.product.lower_tolerance;
      switch (this.product.target_unit) {
        case "KILOGRAM":
          return convertKilogramTo(this.product.lower_tolerance, this.averageProductGiveaway.unit);
        case "LITRE":
          return convertLiterTo(this.product.lower_tolerance, this.averageProductGiveaway.unit);
        case "METER":
          return convertMeterTo(this.product.lower_tolerance, this.averageProductGiveaway.unit);
        default:
          return this.product.lower_tolerance;
      }
    },
    chartYMinMax() {
      const dataPoints = this.convertedDataPoints;

      const initialMin = dataPoints.find((point) => point.value !== null)?.value ?? 0.0;
      const initialMax = initialMin ?? 0.0;

      const dataMin = dataPoints.reduce((min, point) => {
        if (point.value === null) {
          return min;
        }
        return point.value < min ? point.value : min;
      }, initialMin);

      const dataMax = dataPoints.reduce((max, point) => {
        if (point.value === null) {
          return max;
        }
        return point.value > max ? point.value : max;
      }, initialMax);

      const min = this.convertedLowerTolerance ? Math.min(dataMin, this.convertedLowerTolerance) : dataMin;
      const max = this.convertedUpperTolerance ? Math.max(dataMax, this.convertedUpperTolerance) : dataMax;
      // Add 10% of total height in spacing above highest and lowest point
      const diff = (max - min) * 0.1;
      return {
        min: min - diff,
        max: max + diff,
      };
    },
    product() {
      return this.giveawayGraph.products[this.productIndex];
    },
    graphLineColor() {
      return this.theme === "light" ? StyleValues.color_text_themeLight : StyleValues.color_text_themeDark;
    },
    graphBackgroundColor() {
      return this.theme === "light" ? StyleValues.color_control_themeLight : StyleValues.color_control_themeDark;
    },
    labelBackground() {
      return this.theme === "light"
        ? StyleValues.color_baseBackground_themeLight
        : StyleValues.color_baseBackground_themeDark;
    },
    toleranceErrorColor() {
      return this.theme === "light" ? StyleValues.color_errorLight : StyleValues.color_errorDark;
    },
    minTargetLabel() {
      let valueAndUnitLabel = this.getTargetLabel(this.convertedLowerTolerance, this.averageProductGiveaway?.unit);
      let minLabel = this.$t("common.minMax.min");
      return `${minLabel}. ${valueAndUnitLabel}`;
    },
    maxTargetLabel() {
      let valueAndUnitLabel = this.getTargetLabel(this.convertedUpperTolerance, this.averageProductGiveaway?.unit);
      let maxLabel = this.$t("common.minMax.max");
      return `${maxLabel}. ${valueAndUnitLabel}`;
    },
    giveawayChartOptions() {
      return {
        animation: {
          duration: 0,
        },
        title: {
          display: false,
        },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            display: false,
            grid: {
              display: false,
            },
            min: this.chartYMinMax.min,
            max: this.chartYMinMax.max,
          },
          x: {
            type: "linear",
            min: this.giveawayGraph.start.timestamp,
            max: this.giveawayGraph.end.timestamp,
            display: false,
            grid: {
              display: false,
            },
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          annotation: {
            annotations: {
              backgroundProgress: {
                type: "box",
                yMin: this.chartYMinMax.min,
                yMax: this.chartYMinMax.max,
                xMin: this.giveawayGraph.start.timestamp,
                xMax: Math.min(this.currentMillisUTC, this.giveawayGraph.end.timestamp),
                backgroundColor: this.graphBackgroundColor,
                borderWidth: 0,
                drawTime: "beforeDatasetsDraw",
              },
              target: this.convertedTarget
                ? {
                    type: "line",
                    yMin: this.convertedTarget,
                    yMax: this.convertedTarget,
                    borderColor: this.graphLineColor,
                    borderWidth: 1,
                    label: {
                      content: [this.getTargetLabel(this.convertedTarget, this.averageProductGiveaway?.unit)],
                      font: { size: 15, weight: "normal" },
                      enabled: true,
                      position: "end",
                      color: this.graphLineColor,
                      backgroundColor: this.labelBackground,
                      yAdjust: 1,
                      xAdjust: 6,
                      textAlign: "left",
                    },
                    drawTime: "beforeDatasetsDraw",
                  }
                : null,
              lowerTolerance: this.convertedLowerTolerance
                ? {
                    type: "line",
                    yMin: this.convertedLowerTolerance,
                    yMax: this.convertedLowerTolerance,
                    borderColor: this.toleranceErrorColor,
                    borderWidth: 1,
                    borderDash: [3, 3],
                    label: {
                      content: [this.minTargetLabel],
                      font: { size: 15, weight: "normal" },
                      enabled: true,
                      position: "end",
                      color: this.graphLineColor,
                      backgroundColor: this.labelBackground,
                      yAdjust: 1,
                      xAdjust: 6,
                      textAlign: "left",
                    },
                    drawTime: "beforeDatasetsDraw",
                  }
                : null,
              upperTolerance: this.convertedUpperTolerance
                ? {
                    type: "line",
                    yMin: this.convertedUpperTolerance,
                    yMax: this.convertedUpperTolerance,
                    borderColor: this.toleranceErrorColor,
                    borderWidth: 1,
                    borderDash: [3, 3],
                    label: {
                      content: [this.maxTargetLabel],
                      font: { size: 15, weight: "normal" },
                      enabled: true,
                      position: "end",
                      color: this.graphLineColor,
                      backgroundColor: this.labelBackground,
                      yAdjust: 1,
                      xAdjust: 6,
                      textAlign: "left",
                    },
                    drawTime: "beforeDatasetsDraw",
                  }
                : null,
              backgroundDivider: {
                type: "line",
                xMin: this.currentMillisUTC,
                xMax: this.currentMillisUTC,
                borderColor: this.graphLineColor,
                borderWidth: 1,
                label: {
                  enabled: false,
                },
              },
            },
          },
          tooltip: {
            mode: "index",
            intersect: true,
            position: "nearest",
            enabled: true,
            displayColors: false,
            callbacks: {
              title: function title() {
                return i18n.t("tiles.productGiveaway");
              },
              label: function oeePointLabel(tooltipItem) {
                const dataset = tooltipItem.dataset;
                const unit = dataset.utils[tooltipItem.dataIndex].unit;
                const y = dataset.data[tooltipItem.dataIndex].value;
                return `${y.toFixed(2)} ${getUnitLabel(unit)}`;
              },
              footer: function oeePointFooter(tooltipItems) {
                let tooltipItem = tooltipItems != null && tooltipItems instanceof Array ? tooltipItems[0] : null;
                if (tooltipItem) {
                  let dataset = tooltipItem.dataset;
                  if (dataset) {
                    const date = dataset.utils[tooltipItem.dataIndex].date;
                    const time = dataset.utils[tooltipItem.dataIndex].time;
                    return date + "\n" + time;
                  }
                }
                return "";
              },
            },
          },
        },
      };
    },
    giveawayChartData() {
      const pointBackgroundColors = this.convertedDataPoints.map((s) =>
        s.value > this.product.upper_tolerance || s.value < this.product.lower_tolerance
          ? this.toleranceErrorColor
          : this.graphLineColor,
      );
      const utils = this.convertedDataPoints.map((s) => {
        return {
          unit: this.averageProductGiveaway?.unit,
          date: s.date,
          time: s.time,
        };
      });
      return {
        labels: this.convertedDataPoints.map(() => ""),
        datasets: [
          {
            order: 0,
            radius: 3,
            pointRadius: this.isGiveawaySamplingModeActive ? 3 : 0,
            pointHitRadius: 3,
            pointHoverBackgroundColor: pointBackgroundColors,
            pointBackgroundColor: pointBackgroundColors,
            pointBorderColor: this.graphLineColor,
            borderColor: this.graphLineColor,
            borderWidth: 2,
            backgroundColor: "transparent",
            tension: 0.1,
            data: this.convertedDataPoints.map((s) => {
              return {
                x: s.timestamp,
                y: this.getDataYValue(s.value),
                value: s.value,
              };
            }),
            utils,
          },
        ],
      };
    },
  },
  methods: {
    getDataYValue(value) {
      if (!this.product.target_value || !value) return value;
      if (value >= this.product.target_value) {
        return Math.min(value, this.chartYMinMax.max);
      } else {
        return Math.max(value, this.chartYMinMax.min);
      }
    },
    getTargetLabel(value, unit) {
      if (!unit) return "";
      const lowerCaseUnit = unit.toLowerCase();
      const unitLabel = getUnitLabel(lowerCaseUnit);
      return `${value.toFixed(2)} ${unitLabel}`;
    },
    setCurrentTimeUTC() {
      this.currentMillisUTC = new Date(new Date().toISOString()).getTime();
      setInterval(() => {
        this.currentMillisUTC = new Date(new Date().toISOString()).getTime();
      }, 60000);
    },
  },
  mounted() {
    this.setCurrentTimeUTC();
  },
};
</script>

<style lang="scss" scoped>
.giveaway-graph-container {
  flex: 1 0 auto;
  position: relative;
  overflow: hidden;
  margin-top: 10px;

  .canvas-container {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: 1;

    ::v-deep canvas {
      height: 100%;
      width: 100%;
    }
  }
  .x-axe-lines {
    position: absolute;
    left: 0;
    right: 0;
    border-top: 1px solid currentColor;

    &.target-line {
      color: var(--color-text-theme);
      padding-right: 4px;
      text-align: right;
    }
    &.treshold-line {
      color: var(--color-error);
      border-top-width: 2px;
      border-top-style: dashed;
      padding-right: 4px;
      text-align: right;

      &.maximum {
        top: 0;
      }
      &.minimum {
        bottom: 0;
      }
    }
  }
}
</style>
